/**
 * useReturnToOrigin Hook
 * 
 * A composable hook for consuming return-to-origin navigation patterns.
 * Used in pages/components that may be navigated to from another surface
 * with the expectation of returning after completing an action.
 * 
 * ============================================================================
 * USAGE (Batch 4)
 * ============================================================================
 * 
 * In a destination page (e.g., CycleConfig):
 * ```typescript
 * const { origin, hasOrigin, handleReturn, handleCancel } = useReturnToOrigin();
 * 
 * // On successful save:
 * handleReturn({ success: true, entityId: newCriterionId });
 * 
 * // On cancel/close:
 * handleCancel();
 * ```
 * 
 * The hook automatically:
 * 1. Extracts origin from location.state
 * 2. Provides navigation helpers that respect the origin
 * 3. Falls back to a default route if no origin exists
 */

import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIStore } from '../stores/prioritization/uiStore';
import {
  useOriginState,
  returnToOrigin,
  type ReturnResult,
  type NavigationOrigin,
} from '../lib/navigation/returnToOrigin';

export interface UseReturnToOriginOptions {
  /** Default route to navigate to if no origin exists */
  defaultReturnRoute: string;
  /** Whether to show a toast on successful return */
  showSuccessToast?: boolean;
  /** Custom success message */
  successMessage?: string;
}

export interface UseReturnToOriginResult {
  /** The navigation origin, if present */
  origin: NavigationOrigin | undefined;
  /** Whether an origin is present and valid */
  hasOrigin: boolean;
  /** Whether the origin has expired */
  isStale: boolean;
  /** Handle successful completion and return */
  handleReturn: (result: ReturnResult) => void;
  /** Handle cancellation (return without result) */
  handleCancel: () => void;
  /** Context from the origin (cycleId, tab, etc.) */
  originContext: NavigationOrigin['origin_context'];
}

/**
 * Hook for handling return-to-origin navigation in destination pages.
 */
export function useReturnToOrigin({
  defaultReturnRoute,
  showSuccessToast = true,
  successMessage = 'Changes saved successfully',
}: UseReturnToOriginOptions): UseReturnToOriginResult {
  const navigate = useNavigate();
  const { origin, hasOrigin, isStale } = useOriginState();
  const addNotification = useUIStore(state => state.addNotification);
  
  // Handle successful return
  const handleReturn = useCallback((result: ReturnResult) => {
    // Show success notification if enabled
    if (showSuccessToast && result.success) {
      addNotification({
        type: 'success',
        title: 'Success',
        message: successMessage,
      });
    }
    
    // Return to origin or default
    if (origin && !isStale) {
      returnToOrigin(navigate, origin, result);
    } else {
      navigate(defaultReturnRoute, { 
        state: { returnResult: result },
        replace: true,
      });
    }
  }, [origin, isStale, navigate, defaultReturnRoute, showSuccessToast, successMessage, addNotification]);
  
  // Handle cancellation
  const handleCancel = useCallback(() => {
    const cancelResult: ReturnResult = {
      success: false,
      actionType: 'cancelled',
    };
    
    if (origin && !isStale) {
      returnToOrigin(navigate, origin, cancelResult);
    } else {
      navigate(defaultReturnRoute, { replace: true });
    }
  }, [origin, isStale, navigate, defaultReturnRoute]);
  
  return {
    origin,
    hasOrigin: hasOrigin && !isStale,
    isStale,
    handleReturn,
    handleCancel,
    originContext: origin?.origin_context,
  };
}

/**
 * Hook for handling return results after returning TO a page.
 * Use this in the origin page to process the return result.
 */
export function useReturnResultHandler(
  onResult?: (result: ReturnResult) => void
): ReturnResult | undefined {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract return result from location state
  const returnResult = (location.state as { returnResult?: ReturnResult } | undefined)?.returnResult;
  
  // Process result and clear it from state
  useEffect(() => {
    if (returnResult && onResult) {
      onResult(returnResult);
      
      // Clear the return result from location state to prevent re-processing
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...location.state, returnResult: undefined },
      });
    }
  }, [returnResult, onResult, navigate, location.pathname, location.search, location.state]);
  
  return returnResult;
}

export default useReturnToOrigin;
